import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';
import { Passenger } from '@app/models/micro/passenger.model';
import { Reservation } from '@app/models/micro/reservation.model';
import {
  CDC_ATTESTATION_URL,
  getCdcAttestationPassengers,
  hasCdcAttestation,
} from '@app/utils/functions/cdc-attestation';

type CdcAnswer = 'YES' | 'NO';
type CdcRowStatus = 'pending' | 'sending' | 'sent';
type CdcView = 'form' | 'qr' | 'counter' | 'error';

export interface CdcAttestationRow {
  passenger: Passenger;
  answer: CdcAnswer | null;
  confirmed: boolean;
  status: CdcRowStatus;
}

/** Pasajeros por pagina, como en el diseno. */
const PAGE_SIZE = 4;

/**
 * CI-23290: Modal "Declaracion de Salud" (CDC, RDC 21 dias).
 * Una fila por adulto/menor con Si/No, confirmacion de veracidad y su propio "Enviar respuesta".
 * Todos responden; solo al "No" se le asienta el SSR (ATOC = declaro que no estuvo en RDC).
 * Al cerrar con la X, si alguien dijo "Si" toda la reserva va a mostrador.
 * Un error al asentar el SSR avisa que no se pudo procesar y manda a mostrador.
 */
@Component({
  selector: 'app-cdc-attestation-modal',
  templateUrl: './cdc-attestation-modal.component.html',
  styleUrls: ['./cdc-attestation-modal.component.scss'],
  standalone: false,
})
export class CdcAttestationModalComponent implements OnInit {
  @Input() reservation: Reservation;
  @Input() submitFn: (passenger: Passenger) => Observable<boolean> = () => of(false);
  @Input() trackFn: (
    event: NEW_RELIC_EVENT,
    passenger?: Passenger,
    extra?: Record<string, any>
  ) => void = () => {};

  readonly cdcUrl = CDC_ATTESTATION_URL;
  rows: CdcAttestationRow[] = [];
  page = 0;
  view: CdcView = 'form';

  constructor(public modal: NgbActiveModal) {}

  ngOnInit(): void {
    this.rows = getCdcAttestationPassengers(this.reservation).map((passenger) => ({
      passenger,
      answer: null,
      confirmed: false,
      status: hasCdcAttestation(passenger) ? 'sent' : 'pending',
    }));
  }

  get pages(): number[] {
    return Array.from({ length: Math.ceil(this.rows.length / PAGE_SIZE) }, (_, i) => i);
  }

  get pageRows(): CdcAttestationRow[] {
    return this.rows.slice(this.page * PAGE_SIZE, (this.page + 1) * PAGE_SIZE);
  }

  /** La X solo se habilita cuando todos tienen "Respuesta enviada". */
  get allSent(): boolean {
    return this.rows.every((row) => row.status === 'sent');
  }

  get isSending(): boolean {
    return this.rows.some((row) => row.status === 'sending');
  }

  setAnswer(row: CdcAttestationRow, answer: CdcAnswer): void {
    if (row.status !== 'pending') {
      return;
    }
    row.answer = row.answer === answer ? null : answer;
  }

  toggleConfirm(row: CdcAttestationRow): void {
    if (row.status !== 'pending') {
      return;
    }
    row.confirmed = !row.confirmed;
  }

  canSend(row: CdcAttestationRow): boolean {
    return row.status === 'pending' && !!row.answer && row.confirmed && !this.isSending;
  }

  send(row: CdcAttestationRow): void {
    if (!this.canSend(row)) {
      return;
    }
    this.trackFn(NEW_RELIC_EVENT.CDC_ATTESTATION_SUBMIT, row.passenger, { answer: row.answer });

    // Sin SSR: si se le asentara ATOC, al volver al kiosko ya no veria el modal
    if (row.answer === 'YES') {
      row.status = 'sent';
      return;
    }

    row.status = 'sending';
    this.submitFn(row.passenger).subscribe((saved) => {
      if (saved) {
        row.status = 'sent';
        return;
      }
      row.status = 'pending';
      // Aviso de que no se pudo procesar; el mismo mensaje instruye ir a mostrador
      this.goToCounter('SSR_ERROR', row.passenger);
      this.view = 'error';
    });
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.pages.length) {
      this.page = page;
    }
  }

  showDeclaration(): void {
    this.view = 'qr';
  }

  backToForm(): void {
    this.view = 'form';
  }

  close(): void {
    if (!this.allSent) {
      return;
    }
    const declaredYes = this.rows.find((row) => row.answer === 'YES');
    if (declaredYes) {
      this.goToCounter('DECLARED_YES', declaredYes.passenger);
      return;
    }
    this.modal.close('continue');
  }

  finish(): void {
    this.modal.close('counter');
  }

  private goToCounter(reason: string, passenger: Passenger): void {
    this.trackFn(NEW_RELIC_EVENT.CDC_ATTESTATION_COUNTER, passenger, { reason });
    this.view = 'counter';
  }
}
